import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PaymentMethods = ({ paymentMethods, onAddMethod, onUpdateMethod, onDeleteMethod }) => {
  const [isAddingMethod, setIsAddingMethod] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [formData, setFormData] = useState({
    type: 'bank',
    bankName: '',
    iban: '',
    bic: '',
    accountHolder: '',
    paypalEmail: '',
    isDefault: false
  });

  const methodTypeOptions = [
    { value: 'bank', label: 'Virement bancaire' },
    { value: 'paypal', label: 'PayPal' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (editingMethod) {
      onUpdateMethod(editingMethod?.id, formData);
      setEditingMethod(null);
    } else {
      onAddMethod(formData);
      setIsAddingMethod(false);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      type: 'bank',
      bankName: '',
      iban: '',
      bic: '',
      accountHolder: '',
      paypalEmail: '',
      isDefault: false
    });
  };

  const handleEdit = (method) => {
    setEditingMethod(method);
    setFormData(method);
    setIsAddingMethod(true);
  };

  const handleCancel = () => {
    setIsAddingMethod(false);
    setEditingMethod(null);
    resetForm();
  };

  const getMethodIcon = (type) => {
    switch (type) {
      case 'bank':
        return 'Building2';
      case 'paypal':
        return 'CreditCard';
      default:
        return 'Wallet';
    }
  };

  const formatIban = (iban) => {
    return iban?.replace(/(.{4})/g, '$1 ')?.trim();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-card-foreground">Méthodes de paiement</h3>
        <Button
          variant="default"
          onClick={() => setIsAddingMethod(true)}
          iconName="Plus"
          iconPosition="left"
        >
          Ajouter une méthode
        </Button>
      </div>
      {/* Payment Methods List */}
      <div className="space-y-4 mb-6">
        {paymentMethods?.map((method) => (
          <div key={method?.id} className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-2 bg-muted rounded-lg">
                  <Icon name={getMethodIcon(method?.type)} size={20} className="text-muted-foreground" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-card-foreground">
                      {method?.type === 'bank' ? method?.bankName : 'PayPal'}
                    </span>
                    {method?.isDefault && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent text-accent-foreground">
                        Par défaut
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {method?.type === 'bank' ? (
                      <>
                        <div>{method?.accountHolder}</div>
                        <div className="font-mono">{formatIban(method?.iban)}</div>
                      </>
                    ) : (
                      <div>{method?.paypalEmail}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(method)}
                  iconName="Edit"
                >
                  Modifier
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDeleteMethod(method?.id)}
                  iconName="Trash2"
                  className="text-error hover:text-error"
                >
                  Supprimer
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Add/Edit Form */}
      {isAddingMethod && (
        <div className="border border-border rounded-lg p-6 bg-muted/30">
          <h4 className="text-lg font-medium text-card-foreground mb-4">
            {editingMethod ? 'Modifier la méthode de paiement' : 'Ajouter une méthode de paiement'}
          </h4>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Select
                  label="Type de méthode"
                  options={methodTypeOptions}
                  value={formData?.type}
                  onChange={(value) => handleInputChange('type', value)}
                  required
                />
              </div>

              {formData?.type === 'bank' && (
                <>
                  <Input
                    label="Nom de la banque"
                    type="text"
                    value={formData?.bankName}
                    onChange={(e) => handleInputChange('bankName', e?.target?.value)}
                    placeholder="BNP Paribas"
                    required
                  />
                  <Input
                    label="Titulaire du compte"
                    type="text"
                    value={formData?.accountHolder}
                    onChange={(e) => handleInputChange('accountHolder', e?.target?.value)}
                    placeholder="Jean Dupont"
                    required
                  />
                  <Input
                    label="IBAN"
                    type="text"
                    value={formData?.iban}
                    onChange={(e) => handleInputChange('iban', e?.target?.value)}
                    placeholder="FR14 2004 1010 0505 0001 3M02 606"
                    required
                  />
                  <Input
                    label="Code BIC/SWIFT"
                    type="text"
                    value={formData?.bic}
                    onChange={(e) => handleInputChange('bic', e?.target?.value)}
                    placeholder="BNPAFRPP"
                    required
                  />
                </>
              )}

              {formData?.type === 'paypal' && (
                <div className="md:col-span-2">
                  <Input
                    label="Adresse email PayPal"
                    type="email"
                    value={formData?.paypalEmail}
                    onChange={(e) => handleInputChange('paypalEmail', e?.target?.value)}
                    placeholder="votre@email.com"
                    required
                  />
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isDefault"
                checked={formData?.isDefault}
                onChange={(e) => handleInputChange('isDefault', e?.target?.checked)}
                className="rounded border-border"
              />
              <label htmlFor="isDefault" className="text-sm text-card-foreground">
                Définir comme méthode par défaut
              </label>
            </div>

            <div className="flex items-center space-x-4 pt-4">
              <Button type="submit" variant="default">
                {editingMethod ? 'Mettre à jour' : 'Ajouter'}
              </Button>
              <Button type="button" variant="outline" onClick={handleCancel}>
                Annuler
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default PaymentMethods;